import { IManagedUserSummary } from '@/types';
import { ManagedUserTabTableCertifications } from '@/components/Tables/ManagedUserTables/ManagedUserTabTableCertifications';
import { ManagedUserTabTablePractices } from '@/components/Tables/ManagedUserTables/ManagedUserTabTablePractices';
import { ManagedUserTabTableRoleplays } from '@/components/Tables/ManagedUserTables/ManagedUserTabTableRoleplays';
import { SlideoutModalCertificate, SlideoutModalSimulationScore } from '@/components/Modals';
import { useManagedUserProfileTabsFeature } from '@/features/managed-user-dashboard';
import { UserProfileTabs } from '@/components/Users/UserProfileTabs';
import { useGlobalStore } from '@/store';

interface ManagedUserProfileTabModalsProps {
  onClose: () => void;
}

const ManagedUserProfileTabModals: React.FC<ManagedUserProfileTabModalsProps> = ({ onClose }) => {
  const managerActiveUserScorecardIsLoading = useGlobalStore(
    (state) => state.managerActiveUserScorecardIsLoading,
  );

  return (
    <>
      {/* Manager Dashboard Certificate Modal */}
      <SlideoutModalCertificate
        context="Manager"
        isLoading={managerActiveUserScorecardIsLoading}
        onClose={onClose}
      />

      {/* Manager Dashboard Scorecard Sidebar */}
      <SlideoutModalSimulationScore
        context="Manager"
        isLoading={managerActiveUserScorecardIsLoading}
        onClose={onClose}
      />
    </>
  );
};

/**
 * Props for the ManagedUserProfileTabs component, which displays the tabs in a
 * managed user's profile on the manager dashboard.
 */
interface Props {
  /** The managed user whose profile tabs are being displayed */
  managedUser?: IManagedUserSummary | null;
  /** The ID of the managed user */
  managedUserId?: string | null;
  /** Callback function to handle returning to the dashboard */
  onReturnToDashboard?: () => void;
  /** The session ID for the current user session */
  sessionId?: string | null;
}

/**
 * Component for displaying the tabs in a managed user's profile on the manager dashboard.
 * This includes certifications, practices, and role-plays, as well as the ability to view
 * scorecards in a sidebar and print them to PDF. The component also includes filtering by product
 * and handles loading states and error handling for API calls.
 */
export const ManagedUserProfileTabs: React.FC<Props> = ({
  managedUser,
  managedUserId,
  onReturnToDashboard,
  sessionId,
}) => {
  // =============================================================================
  // Feature hook for managing state and handlers related to the
  // managed user profile tabs
  // =============================================================================
  const {
    managerActiveUserIsLoading,
    returnToDashboardLabel,
    sharedProps,
    tabs,
    handleScoreSidebarClose,
  } = useManagedUserProfileTabsFeature({ managedUser, managedUserId, sessionId });

  // =============================================================================
  // Render the user profile tabs with the certification, practice, and role-play tables,
  // as well as the scorecard sidebar and certificate modal. The tables receive shared props
  // for consistent handling of product filtering and viewing certificates/scorecards.
  // =============================================================================
  return (
    <>
      {/* Manager Dashboard User Profile Tabs */}
      <UserProfileTabs
        context="Manager"
        isLoading={managerActiveUserIsLoading}
        onReturnToDashboard={onReturnToDashboard}
        returnToDashboardLabel={returnToDashboardLabel}
        tabs={tabs}
        user={managedUser as IManagedUserSummary}
        userObjectType="IManagedUserSummary"
      >
        <ManagedUserTabTableCertifications {...sharedProps} />
        <ManagedUserTabTablePractices {...sharedProps} />
        <ManagedUserTabTableRoleplays {...sharedProps} />
      </UserProfileTabs>

      <ManagedUserProfileTabModals onClose={handleScoreSidebarClose} />
    </>
  );
};

ManagedUserProfileTabs.displayName = 'ManagedUserProfileTabs';

export default ManagedUserProfileTabs;
